/**
 * Member card QR payload helpers (encode / decode).
 * Shared by LIFF my-member-card and Manual (non-LIFF) tools.
 *
 * Format: `{obfuscate(cardCode|yyMMddHHmmss)}${licensePlate}`
 * Expiry is typically now + 10 minutes when encoding.
 */

export function formatDateForKey(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    pad(date.getFullYear() % 100) +
    pad(date.getMonth() + 1) +
    pad(date.getDate()) +
    pad(date.getHours()) +
    pad(date.getMinutes()) +
    pad(date.getSeconds())
  );
}

export function obfuscate(input: string): string {
  const junkChars = ['X', 'Y', 'Z'];
  const parts = input.split('|');
  if (parts.length !== 2) return input;

  const uuid = parts[0];
  const datetime = parts[1];
  const reversedUuid = uuid.split('').reverse().join('');
  const reversedDate = datetime.split('').reverse().join('');
  const merged = `${reversedDate}|${reversedUuid}`;

  let result = '';
  for (let i = 0; i < merged.length; i++) {
    result += merged[i];
    if ((i + 1) % 4 === 0) {
      result += junkChars[Math.floor(Math.random() * junkChars.length)];
    }
  }
  return result;
}

/** Decode obfuscated segment only (no `$license`). */
export function deobfuscate(input: string): string {
  const cleaned = input.replace(/[XYZ]/g, '');
  const parts = cleaned.split('|');
  if (parts.length !== 2) return input;

  const datetime = parts[0].split('').reverse().join('');
  const uuid = parts[1].split('').reverse().join('');
  return `${uuid}|${datetime}`;
}

export function buildMemberQrPayload(
  cardCode: string,
  licensePlate: string,
  expireAt: Date = (() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 10);
    return d;
  })(),
): string {
  const raw = `${cardCode}|${formatDateForKey(expireAt)}`;
  return obfuscate(raw) + '$' + licensePlate;
}

export type DecodedMemberQr = {
  cardCode: string;
  expireYyMMddHHmmss: string;
  licensePlate: string;
};

export function decodeMemberQrPayload(fullInput: string): DecodedMemberQr | null {
  const dollar = fullInput.lastIndexOf('$');
  if (dollar < 0) return null;

  const obfuscated = fullInput.slice(0, dollar);
  const licensePlate = fullInput.slice(dollar + 1);
  const cleaned = obfuscated.replace(/[XYZ]/g, '');
  const parts = cleaned.split('|');
  if (parts.length !== 2) return null;

  return {
    expireYyMMddHHmmss: parts[0].split('').reverse().join(''),
    cardCode: parts[1].split('').reverse().join(''),
    licensePlate,
  };
}
